import React from 'react';

interface GuideModalProps {
  onClose: () => void;
}

const GuideModal: React.FC<GuideModalProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="fixed inset-0 bg-black bg-opacity-30"
        onClick={onClose}
        aria-label="가이드 닫기"
      />
      <aside className="relative w-full max-w-xl h-full bg-white shadow-xl flex flex-col animate-slide-in-right">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 z-10"
          aria-label="닫기"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="overflow-y-auto h-full px-8 py-10">
          <h1 className="text-2xl font-bold text-center mb-8">Aitag AI 사용 가이드</h1>
          <hr className="mb-6" />
          <h2 className="text-base font-bold text-gray-900 mb-2">Aitag AI의 주요 특징</h2>
          <ul className="list-disc pl-5 mb-6 text-gray-800 text-sm leading-relaxed space-y-1">
            <li><b>맥락 기반 설명 생성</b> <br />작성된 콘텐츠의 내용을 반영하여 이미지의 인물, 배경, 상황 등을 설명합니다. 단순 요약이 아닌, 문맥 기반 해석을 제공합니다.</li>
            <li><b>인물 정보 및 감정 표현</b> <br />인물의 외형, 표정, 의상, 동작 등을 정확히 기술하여 시각적 요소를 풍부하게 전달합니다.</li>
            <li><b>표 및 그래프 해석</b> <br />도표, 수치, 추세 등의 시각 자료를 시각장애인도 이해할 수 있도록 의도를 반영한 구조적인 설명을 생성합니다.</li>
            <li><b>텍스트 인식 정확도</b> <br />이미지 내 삽입된 텍스트를 정확히 추출하고 문장 구조로 재구성합니다. 텍스트 인식 오류와 환각(hallucination)을 최소화합니다.</li>
            <li><b>스크린리더 최적화 표현 방식 적용</b> <br />예: "ml" 등의 단위 발음을 스크린리더가 인식하기 쉬운 방식으로 자동 변환하여 정확하고 듣기 편하게 정보를 전달합니다.</li>
          </ul>
          <hr className="mb-6" />
          <h2 className="text-base font-bold text-gray-900 mb-2">사용 방법</h2>
          <ol className="list-decimal pl-5 mb-6 text-gray-800 text-sm leading-relaxed space-y-1">
            <li><b>콘텐츠 작성</b> <br />본문 영역에 콘텐츠 내용을 자유롭게 입력합니다. (ex. 블로그 글, 보도자료, 뉴스, 정보성 글 등)<br />작성된 내용은 이미지 대체 텍스트 생성 시 문맥 분석에 활용됩니다.</li>
            <li><b>이미지 업로드</b> <br />사진 추가 버튼을 클릭하여 콘텐츠 본문에 이미지를 업로드합니다.<br />업로드된 이미지는 자동으로 우측 '대체 텍스트 생성' 영역(라이브러리)에 정렬되어 표시됩니다.</li>
            <li><b>대체 텍스트 생성 설정</b> <br />우측 상단의 상세 설정 버튼을 눌러 다음 옵션을 설정할 수 있습니다:<br />- 언어: 한글 / 영어 중 선택<br />- 문체: informal / honorific / polite / plain 중 선택<br />- 분량: 최대 분량 설정</li>
            <li><b>대체 텍스트 일괄 생성</b> <br />설정 완료 후 우측 하단의 생성 버튼을 누르면 모든 이미지에 대해 대체 텍스트가 일괄 자동 생성됩니다.<br />생성된 텍스트는 문맥을 반영하여 이미지 내용을 정확하고 풍부하게 설명합니다.</li>
            <li><b>대체 텍스트 확인 및 수정</b> <br />각 이미지 썸네일을 클릭하면 상세페이지로 이동하여 생성된 대체 텍스트를 직접 확인하고 자유롭게 수정할 수 있습니다.</li>
          </ol>
          <hr className="mb-6" />
          <h2 className="text-base font-bold text-gray-900 mb-2">자주 묻는 질문 (FAQ)</h2>
          <ul className="list-disc pl-5 mb-6 text-gray-800 text-sm leading-relaxed space-y-1">
            <li>지원하는 이미지 형식은 무엇인가요?<br />ㄴ JPG, JPEG, PNG, WEBP 형식을 지원합니다.</li>
            <li>이미지 용량 제한이 있나요?<br />ㄴ 최대 30MB 이하의 이미지만 업로드할 수 있습니다.</li>
            <li>콘텐츠 내용 없이 이미지만 업로드해도 생성되나요?<br />ㄴ 네. 문맥 정보가 없어도 이미지에 대한 풍부한 설명이 제공됩니다.</li>
          </ul>
          <hr className="mb-6" />
          <h2 className="text-base font-bold text-gray-900 mb-2">유의사항</h2>
          <ul className="list-disc pl-5 mb-6 text-gray-800 text-sm leading-relaxed space-y-1">
            <li>이 데모는 CMS 또는 웹 에디터 환경을 기반으로 설계되었으며, 실제 서비스 연동과는 차이가 있을 수 있습니다.</li>
            <li>AI는 이미지 및 텍스트의 조합에 따라 설명 품질이 달라질 수 있습니다. 민감하거나 복잡한 이미지에 대해서는 검토자 확인을 권장합니다.</li>
            <li>생성된 설명은 접근성 기준 및 스크린리더 사용을 고려한 형식으로 제공되지만, 사용자의 콘텐츠 유형에 따라 추가 보완이 필요할 수 있습니다.</li>
          </ul>
          <hr className="mb-6" />
          <h2 className="text-base font-bold text-gray-900 mb-2">
            모두를 위한 디지털 일상, <span className="text-blue-700">Aitag</span>
          </h2>
          <p className="text-gray-800 text-sm">
            에이택은 누구나 정보에 접근할 수 있도록, 보다 정확하고 포용적인 디지털 환경을 만들어갑니다.
          </p>
        </div>
      </aside>
    </div>
  );
};

export default GuideModal; 